import { useState } from 'react'
import { useBuildBuilding, useTavernBlueprint } from '@/api/hooks'
import { Card, GoldButton } from '@/components/game/ui'
import { BuildingIcon, GoldIcon, ChevronDown } from '@/components/game/GameIcons'
import { TavernProgressionTable } from '@/components/TavernProgressionTable'
import { formatDuration } from '@/lib/time'

/**
 * Ghost card shown on Mon île while the Tavern hasn't been built yet.
 * Cost and build time come from the blueprint (single source of truth).
 * Falls back to hardcoded values while the blueprint is loading.
 */
const TAVERN_BUILD_COST_GOLD_FALLBACK = 150

interface Props {
  /** Fronts de chantier déjà ouverts sur l'île. */
  slotsUsed?: number
  /** Créneaux accordés par le niveau de QG (1 en dessous du QG 18). */
  slots?: number
  /** Multiplicateur de prix du prochain chantier, calculé serveur. */
  premiumMultiplier?: number
}

export function BuildTavernCard({ slotsUsed = 0, slots = 1, premiumMultiplier = 1 }: Props = {}) {
  const build = useBuildBuilding()
  const { data: blueprint } = useTavernBlueprint()
  const [showProgression, setShowProgression] = useState(false)

  const nominalCost = blueprint?.buildCostGold ?? TAVERN_BUILD_COST_GOLD_FALLBACK
  // Construire consomme un créneau : même surcoût qu'une amélioration. On ne
  // recalcule rien ici, on applique le multiplicateur fourni par le serveur.
  const isPremium = premiumMultiplier > 1
  const buildCost = Math.round(nominalCost * premiumMultiplier)
  const buildTime = blueprint?.buildTimeSeconds
  const slotsFull = slotsUsed >= slots

  return (
    <Card className="p-5 flex flex-col gap-3 border-dashed">
      <header className="flex items-center gap-2.5">
        <BuildingIcon type="tavern" className="w-6 h-6 text-sand-300" />
        <h3 className="font-display text-lg text-sand-300">Taverne</h3>
        <span className="text-[10px] uppercase tracking-wider rounded-full border border-sand-500/25 px-2 py-0.5 text-ocean-300">
          À construire
        </span>
      </header>
      <p className="text-xs text-ocean-200">
        Le tableau des quêtes de ton île : on y propose des affaires, on y paie des tournées.
      </p>
      <div className="flex items-center justify-between gap-2 mt-auto">
        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums">
            {isPremium && <span className="line-through text-ocean-100/45">{nominalCost}</span>}
            <GoldIcon className="w-4 h-4 text-sand-300" />
            <span className={isPremium ? 'text-amber-300' : 'text-sand-200'}>{buildCost}</span>
            {isPremium && (
              <span className="inline-flex items-center rounded border border-amber-400/50 bg-amber-400/10 px-1 py-px text-[0.65rem] font-bold text-amber-300">
                ×{premiumMultiplier.toLocaleString('fr-FR')}
              </span>
            )}
          </span>
          {buildTime !== undefined && (
            <span className="inline-flex items-center gap-1 text-xs text-ocean-300">
              <span aria-hidden="true">⏱</span>
              {' '}Construction : {formatDuration(buildTime)}
            </span>
          )}
        </div>
        <GoldButton
          onClick={() => build.mutate({ type: 'tavern' })}
          disabled={build.isPending || slotsFull}
          title={slotsFull ? 'Aucun créneau de chantier libre' : 'Construire la Taverne'}
          className="text-sm px-3 py-1.5"
        >
          {slotsFull ? (slots <= 1 ? 'File occupée' : 'Créneaux occupés') : 'Construire'}
        </GoldButton>
      </div>

      {blueprint && (
        <div className="pt-1 border-t border-sand-500/12">
          <button
            type="button"
            onClick={() => setShowProgression((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-ocean-300 hover:text-sand-300 transition-colors"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showProgression ? 'rotate-180' : ''}`} />
            {showProgression ? "Masquer l'évolution" : "Voir l'évolution par niveau"}
          </button>
          {showProgression && (
            <TavernProgressionTable progression={blueprint.progression} />
          )}
        </div>
      )}
    </Card>
  )
}
